// ============================================================================
// QUOTATION REQUESTS TAB
// Displays quotation requests linked to work items (appointments and jobs)
// Shows APT# badge for requests inherited from child appointments
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, ExternalLink, Clock, CheckCircle, XCircle, Hash, Loader2 } from 'lucide-react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { formatDateSAST } from '@/lib/sastTime';

// ============================================================================
// TYPES
// ============================================================================

interface QuotationRequest {
  id: string;
  item_number?: string;
  title?: string;
  status?: string;
  created_at?: string;
  description?: string;
  source_appointment_number?: string;
  source_appointment_id?: string;
}

interface QuotationRequestsTabProps {
  workItemId: string;
  itemType: 'job' | 'appointment';
  isActive?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function QuotationRequestsTab({ workItemId, itemType, isActive }: QuotationRequestsTabProps) {
  const [requests, setRequests] = useState<QuotationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getHeaders = useCallback(() => {
    const token = authService.getTokens()?.access_token;
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadQuotationRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = getHeaders();

      // Fetch relationships to find linked quote requests
      const relationshipsResponse = await fetch(
        getApiUrl(`/api/v1/work-items/${workItemId}/relationships`),
        { headers }
      );

      if (!relationshipsResponse.ok) {
        throw new Error('Failed to load relationships');
      }

      const relationshipsData = await relationshipsResponse.json();
      const relationships = Array.isArray(relationshipsData.data) ? relationshipsData.data : [];

      const quoteRequestIds = new Set<string>();
      const appointmentToQuoteRequestMap = new Map<string, string[]>(); // appointment_id -> quote_request_ids

      // Find all quote request relationships
      for (const rel of relationships) {
        const sourceId = rel.source_work_item_id;
        const targetId = rel.target_work_item_id;
        const sourceWorkItem = rel.source_work_item;
        const targetWorkItem = rel.target_work_item;

        // Check if either side is a quote request
        if (sourceWorkItem?.item_type === 'quote_request' && targetId === workItemId) {
          quoteRequestIds.add(sourceId);
          // Track which appointment this quote request came from
          if (sourceWorkItem.source_appointment_id) {
            if (!appointmentToQuoteRequestMap.has(sourceWorkItem.source_appointment_id)) {
              appointmentToQuoteRequestMap.set(sourceWorkItem.source_appointment_id, []);
            }
            appointmentToQuoteRequestMap.get(sourceWorkItem.source_appointment_id)!.push(sourceId);
          }
        } else if (targetWorkItem?.item_type === 'quote_request' && sourceId === workItemId) {
          quoteRequestIds.add(targetId);
          // Track which appointment this quote request came from
          if (targetWorkItem.source_appointment_id) {
            if (!appointmentToQuoteRequestMap.has(targetWorkItem.source_appointment_id)) {
              appointmentToQuoteRequestMap.set(targetWorkItem.source_appointment_id, []);
            }
            appointmentToQuoteRequestMap.get(targetWorkItem.source_appointment_id)!.push(targetId);
          }
        }
      }

      // For jobs, also fetch quote requests from child appointments
      if (itemType === 'job') {
        const appointmentsResponse = await fetch(
          getApiUrl(`/api/v1/work-items?item_type=appointment&parent_id=${workItemId}`),
          { headers }
        );

        if (appointmentsResponse.ok) {
          const appointmentsData = await appointmentsResponse.json();
          const appointments = Array.isArray(appointmentsData.data) ? appointmentsData.data : [];

          for (const appointment of appointments) {
            // Fetch relationships for each child appointment
            const appointmentRelationshipsResponse = await fetch(
              getApiUrl(`/api/v1/work-items/${appointment.id}/relationships`),
              { headers }
            );

            if (appointmentRelationshipsResponse.ok) {
              const appointmentRelationshipsData = await appointmentRelationshipsResponse.json();
              const appointmentRelationships = Array.isArray(appointmentRelationshipsData.data) ? appointmentRelationshipsData.data : [];

              for (const rel of appointmentRelationships) {
                const sourceWorkItem = rel.source_work_item;
                const targetWorkItem = rel.target_work_item;

                if (sourceWorkItem?.item_type === 'quote_request' && rel.source_work_item_id !== workItemId) {
                  quoteRequestIds.add(sourceWorkItem.id);
                  // Track that this came from this appointment
                  if (!appointmentToQuoteRequestMap.has(appointment.id)) {
                    appointmentToQuoteRequestMap.set(appointment.id, []);
                  }
                  appointmentToQuoteRequestMap.get(appointment.id)!.push(sourceWorkItem.id);
                } else if (targetWorkItem?.item_type === 'quote_request' && rel.target_work_item_id !== workItemId) {
                  quoteRequestIds.add(targetWorkItem.id);
                  // Track that this came from this appointment
                  if (!appointmentToQuoteRequestMap.has(appointment.id)) {
                    appointmentToQuoteRequestMap.set(appointment.id, []);
                  }
                  appointmentToQuoteRequestMap.get(appointment.id)!.push(targetWorkItem.id);
                }
              }
            }
          }
        }
      }

      // Fetch full details for all quote requests
      const quoteRequests: QuotationRequest[] = [];
      for (const id of quoteRequestIds) {
        try {
          const quoteResponse = await fetch(getApiUrl(`/api/v1/work-items/${id}`), { headers });
          if (quoteResponse.ok) {
            const quoteData = await quoteResponse.json();
            const quote = quoteData.data;

            // Find which appointment this quote request came from
            let sourceAppointmentNumber: string | undefined;
            let sourceAppointmentId: string | undefined;
            for (const [aptId, quoteIds] of appointmentToQuoteRequestMap.entries()) {
              if (quoteIds.includes(id)) {
                // Fetch appointment details to get the number
                const aptResponse = await fetch(getApiUrl(`/api/v1/work-items/${aptId}`), { headers });
                if (aptResponse.ok) {
                  const aptData = await aptResponse.json();
                  sourceAppointmentNumber = aptData.data?.item_number;
                  sourceAppointmentId = aptId;
                }
                break;
              }
            }

            quoteRequests.push({
              id: quote.id,
              item_number: quote.item_number,
              title: quote.title,
              status: quote.status,
              created_at: quote.created_at,
              description: quote.description,
              source_appointment_number: sourceAppointmentNumber,
              source_appointment_id: sourceAppointmentId,
            });
          }
        } catch (err) {
          console.error('Failed to load quote request details:', err);
        }
      }

      setRequests(quoteRequests);
    } catch (err) {
      console.error('Error loading quotation requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quotation requests');
    } finally {
      setLoading(false);
    }
  }, [workItemId, itemType, getHeaders]);

  useEffect(() => {
    if (isActive) {
      loadQuotationRequests();
    }
  }, [isActive, loadQuotationRequests]);

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'submitted':
        return { color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Submitted' };
      case 'under_review':
        return { color: 'text-yellow-600', bg: 'bg-yellow-100 dark:bg-yellow-900/30', label: 'Under Review' };
      case 'pricing_added':
        return { color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Pricing Added' };
      case 'converted':
        return { color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Converted to Quote' };
      case 'cancelled':
        return { color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-800', label: 'Cancelled' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-100 dark:bg-gray-800', label: status || 'Draft' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <FileText className="w-12 h-12 text-gray-400 mb-3" />
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          No quotation requests yet
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {itemType === 'appointment'
            ? 'Create quotation requests from the Quote Request tab to track items that need pricing.'
            : 'Quotation requests from child appointments will appear here once created.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((request) => {
        const statusBadge = getStatusBadge(request.status);
        return (
          <div
            key={request.id}
            className="border rounded-lg p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {request.title || 'Untitled Quote Request'}
                  </h4>
                  {request.item_number && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      #{request.item_number}
                    </span>
                  )}
                  {request.source_appointment_number && (
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      APT{request.source_appointment_number}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadge.bg} ${statusBadge.color}`}>
                    {statusBadge.label}
                  </span>
                  {request.created_at && (
                    <span>{formatDateSAST(request.created_at)}</span>
                  )}
                </div>
              </div>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="View quotation request"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            {request.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {request.description}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
